'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["connectDragSource", "disabled", "didDrop", "dropItem", "disabledMessage", "savedSection"];
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DragSource } from 'react-dnd-compat';
import omit from 'hs-lodash/omit';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import DraggableTile from './DraggableTile';
import { SAVE_SECTION } from 'LayoutBuilderUI/lib/DragDropConstants';
import { SECTION_ICON_MAX_SIZE } from 'EmailEditorUI/constants/SidebarConstants';
import { logInteraction } from 'LayoutBuilderUI/actions/ApiActions';
import { getLayoutLoggingType } from 'EmailEditorUI/utils/usageTracking';
import { dragSavedSection, openFullWidthExitModal } from 'LayoutBuilderUI/actions/layoutActions';
import { dropSavedSectionOnSection } from 'LayoutBuilderUI/actions/ApiActions';
import { getFooterSectionIndex } from 'EmailEditorUI/redux/selectors/ModuleSelectors';
import { getSections } from 'EmailEditorUI/redux/selectors/sectionSelectors';
import { getIsSingleColumnFullWidth } from 'EmailEditorUI/redux/selectors/styleSettingsSelectors';
import { DropPosition } from 'EmailEditorUI/redux/actions/types/layoutActions';
import { SectionIcon } from 'foundations-assets/icons/components/SectionIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const layoutDragSource = {
  beginDrag(props, monitor, component) {
    props.dragSavedSection();
    return {
      key: props.savedSection.label,
      savedSectionPath: props.savedSection.path,
      dragItemRef: component.dragItemRef,
      component,
      shouldUsePreview: false
    };
  },
  endDrag(props, monitor, component) {
    if (!monitor.didDrop()) {
      return;
    }
    const dropResult = monitor.getDropResult();
    const item = monitor.getItem();
    if (props.isSingleColumnFullWidth) {
      component.showFullWidthExitModal(dropResult);
    } else {
      logInteraction({
        action: 'Modify Layout Action',
        event: 'add-saved-section'
      });
      dropSavedSectionOnSection({
        sectionPath: item.savedSectionPath,
        dropPosition: dropResult.dropPosition,
        forceDropOutsideBoxed: dropResult.forceDropOutsideBoxed,
        targetSectionIndex: dropResult.sectionIndex
      });
    }
  }
};
const OMIT_PROPS = ['openFullWidthExitModal', 'dragSavedSection', 'isSingleColumnFullWidth'];
class LayoutTile extends Component {
  constructor(props) {
    super(props);
    this.onDoubleClick = this.onDoubleClick.bind(this);
  }
  onDoubleClick() {
    if (this.props.disabled) return;
    if (this.props.isSingleColumnFullWidth) {
      this.showFullWidthExitModal();
    } else {
      logInteraction({
        action: 'Modify Layout Action',
        event: 'add-section-double-click',
        'section-path': getLayoutLoggingType(this.props.savedSection.path)
      });
      dropSavedSectionOnSection({
        sectionPath: this.props.savedSection.path,
        dropPosition: DropPosition.AFTER,
        targetSectionIndex: this.props.sectionIndexForClickDrop,
        forceDropOutsideBoxed: false
      });
    }
  }
  showFullWidthExitModal(dropResult = {}) {
    const {
      dropPosition = DropPosition.AFTER,
      sectionIndex = this.props.sectionIndexForClickDrop,
      forceDropOutsideBoxed = false
    } = dropResult;
    this.props.openFullWidthExitModal({
      sectionPath: this.props.savedSection.path,
      dropPosition,
      targetSectionIndex: sectionIndex,
      forceDropOutsideBoxed
    });
  }
  renderLabel() {
    const {
      savedSection
    } = this.props;
    return savedSection.label || '';
  }
  renderIcon() {
    var _savedSection$userMet;
    const {
      savedSection
    } = this.props;
    const thumbnail = (_savedSection$userMet = savedSection.userMeta) === null || _savedSection$userMet === void 0 ? void 0 : _savedSection$userMet.thumbnailPath;
    return thumbnail ? /*#__PURE__*/_jsx(Image, {
      role: "none",
      src: thumbnail,
      maxInlineSize: "100%",
      maxBlockSize: "80px",
      objectFit: "contain"
    }) : /*#__PURE__*/_jsx(IconHolder, {
      padding: "0",
      color: "icon-core-subtle",
      icon: SectionIcon,
      size: SECTION_ICON_MAX_SIZE
    });
  }
  render() {
    const _this$props = this.props,
      {
        connectDragSource,
        disabled,
        didDrop,
        dropItem,
        disabledMessage,
        savedSection
      } = _this$props,
      otherProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const key = savedSection.label;
    const didDropThis = didDrop && dropItem.key === key;
    return /*#__PURE__*/_jsx(DraggableTile, Object.assign({
      disabled: disabled,
      disabledMessage: disabledMessage,
      icon: this.renderIcon(),
      label: this.renderLabel(),
      didDrop: didDropThis,
      onDoubleClick: this.onDoubleClick,
      innerRef: el => {
        if (!disabled) {
          this.dragItemRef = el;
          connectDragSource(el);
        }
      },
      wrapLabel: true,
      tileSize: "medium"
    }, omit(otherProps, OMIT_PROPS)));
  }
}
LayoutTile.propTypes = {
  savedSection: PropTypes.object,
  disabled: PropTypes.bool,
  openFullWidthExitModal: PropTypes.func.isRequired,
  // react-dnd
  connectDragSource: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  didDrop: PropTypes.bool.isRequired,
  dropItem: PropTypes.object,
  disabledMessage: PropTypes.element,
  sectionIndexForClickDrop: PropTypes.number.isRequired,
  previewDomain: PropTypes.string,
  isSingleColumnFullWidth: PropTypes.bool
};
const Draggable = DragSource(SAVE_SECTION, layoutDragSource, monitor => ({
  didDrop: monitor.didDrop(),
  dropItem: monitor.getItem()
}));
const mapStateToProps = state => ({
  sectionIndexForClickDrop: getFooterSectionIndex(state) || getSections(state).size,
  isSingleColumnFullWidth: getIsSingleColumnFullWidth(state)
});
const mapDispatchToProps = {
  openFullWidthExitModal,
  dragSavedSection
};
export default connect(mapStateToProps, mapDispatchToProps)(Draggable(LayoutTile));